import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  CircularProgress,
  Alert,
  Chip,
  InputAdornment,
  IconButton,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider
} from '@mui/material';
import {
  Timeline as CaboIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  CompareArrows as PassagemIcon,
  Input as InputIcon,
  Output as OutputIcon
} from '@mui/icons-material';
import { Cabo, caboService } from '../../services/caboService';
import { toast } from 'react-toastify';

// Enum para tipos de cabo
enum TipoCabo {
  NORMAL = 'normal',
  PASSAGEM = 'passagem',
  SAIDA = 'saida'
}

interface CaboSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSelectCabo: (cabo: Cabo, tipoCabo?: string) => void;
  isFullscreen?: boolean;
  /** Quando em tela cheia, container para o Dialog aparecer dentro do elemento fullscreen */
  container?: HTMLElement | null;
}

const CaboSelectionModal: React.FC<CaboSelectionModalProps> = ({
  open,
  onClose,
  onSelectCabo,
  isFullscreen = false,
  container
}) => {
  const [cabos, setCabos] = useState<Cabo[]>([]);
  const [filteredCabos, setFilteredCabos] = useState<Cabo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [tipoCabo, setTipoCabo] = useState<TipoCabo>(TipoCabo.NORMAL);
  const [selectedCabo, setSelectedCabo] = useState<Cabo | null>(null);

  // Carregar cabos do backend
  const loadCabos = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 Carregando cabos...');
      const response = await caboService.getCabos({ limit: 100 }); // Carregar até 100 cabos
      console.log('📊 Resposta completa:', response);
      
      // Verificar diferentes estruturas de resposta devido ao interceptor
      let cabosData: Cabo[] = [];
      
      if (response && response.cabos && Array.isArray(response.cabos)) {
        // Estrutura normal: { cabos: [...], totalItems, ... }
        cabosData = response.cabos;
        console.log('✅ Cabos encontrados na estrutura normal:', cabosData.length);
      } else if (response && Array.isArray(response)) {
        // Resposta direta como array
        cabosData = response;
        console.log('✅ Cabos encontrados como array direto:', cabosData.length);
      } else if (response && (response as any).data && (response as any).data.cabos && Array.isArray((response as any).data.cabos)) {
        // Estrutura nested: { data: { cabos: [...] } }
        cabosData = (response as any).data.cabos;
        console.log('✅ Cabos encontrados na estrutura nested:', cabosData.length);
      } else {
        console.warn('⚠️ Estrutura de resposta inesperada:', response);
        throw new Error('Formato de resposta inválido');
      }
      
      setCabos(cabosData);
      setFilteredCabos(cabosData);
      console.log(`✅ ${cabosData.length} cabos carregados com sucesso`);
      
    } catch (err: any) {
      console.error('❌ Erro ao carregar cabos:', err);
      setError(`Erro ao carregar cabos: ${err.message || 'Erro desconhecido'}`);
      toast.error('Erro ao carregar lista de cabos');
    } finally {
      setLoading(false);
    }
  };

  // Filtrar cabos baseado na busca
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredCabos(cabos);
    } else {
      const filtered = cabos.filter(cabo =>
        cabo.titulo.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCabos(filtered);
    }
  }, [searchTerm, cabos]);

  // Carregar cabos quando abrir modal
  useEffect(() => {
    if (open) {
      loadCabos();
      setSearchTerm(''); // Limpar busca
    }
  }, [open]);

  // Handler para selecionar cabo
  const handleSelectCabo = (cabo: Cabo) => {
    setSelectedCabo(cabo);
  };

  // Handler para confirmar seleção
  const handleConfirmSelection = () => {
    if (selectedCabo) {
      onSelectCabo(selectedCabo, tipoCabo);
      onClose();
    }
  };

  // Handler para fechar modal
  const handleClose = () => {
    setSearchTerm('');
    setTipoCabo(TipoCabo.NORMAL);
    setSelectedCabo(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      container={isFullscreen && container ? container : undefined}
      BackdropProps={{ sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)' } }}
      sx={{
        zIndex: isFullscreen ? 10003 : 1300, // Z-index maior que o menu de adicionar componentes
      }}
      PaperProps={{
        sx: { height: '600px' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CaboIcon />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Selecionar Cabo
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {/* Campo de busca */}
        <TextField
          fullWidth
          placeholder="Buscar cabo por título..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        {/* Seleção do tipo de cabo */}
        <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>
              Tipo de Cabo
            </FormLabel>
            <RadioGroup
              value={tipoCabo}
              onChange={(e) => setTipoCabo(e.target.value as TipoCabo)}
              sx={{ gap: 1 }}
            >
              <FormControlLabel
                value={TipoCabo.NORMAL}
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InputIcon sx={{ fontSize: 20 }} />
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Cabo Normal (Entrada)
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Adiciona um cabo com saídas
                      </Typography>
                    </Box>
                  </Box>
                }
              />
              <FormControlLabel
                value={TipoCabo.PASSAGEM}
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PassagemIcon sx={{ fontSize: 20 }} />
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Cabo de Passagem
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Cria 2 cabos: um normal (com saídas) + um de passagem (apenas entradas)
                      </Typography>
                    </Box>
                  </Box>
                }
              />
              <FormControlLabel
                value={TipoCabo.SAIDA}
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <OutputIcon sx={{ fontSize: 20 }} />
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Cabo de Saída
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Adiciona apenas um cabo de saída (apenas entradas)
                      </Typography>
                    </Box>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Loading */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Carregando cabos...</Typography>
          </Box>
        )}

        {/* Error */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 2 }}
            action={
              <IconButton onClick={loadCabos} size="small">
                <RefreshIcon />
              </IconButton>
            }
          >
            {error}
          </Alert>
        )}

        {/* Lista de cabos */}
        {!loading && !error && (
          <>
            {/* Info sobre resultados */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {filteredCabos.length} cabo{filteredCabos.length !== 1 ? 's' : ''} encontrado{filteredCabos.length !== 1 ? 's' : ''}
                {searchTerm && ` para "${searchTerm}"`}
              </Typography>
            </Box>

            {/* Lista */}
            <List sx={{ maxHeight: '400px', overflow: 'auto' }}>
              {filteredCabos.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CaboIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    {searchTerm ? 'Nenhum cabo encontrado' : 'Nenhum cabo cadastrado'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {searchTerm 
                      ? 'Tente buscar com outros termos'
                      : 'Cadastre cabos primeiro para poder adicioná-los ao diagrama'
                    }
                  </Typography>
                </Box>
              ) : (
                filteredCabos.map((cabo) => (
                  <ListItem key={cabo.id} disablePadding>
                    <ListItemButton
                      onClick={() => handleSelectCabo(cabo)}
                      selected={selectedCabo?.id === cabo.id}
                      sx={{
                        borderRadius: 1,
                        mb: 1,
                        border: '1px solid',
                        borderColor: selectedCabo?.id === cabo.id ? 'primary.main' : 'divider',
                        backgroundColor: selectedCabo?.id === cabo.id ? 'primary.50' : 'transparent',
                        '&:hover': {
                          borderColor: 'primary.main',
                          backgroundColor: selectedCabo?.id === cabo.id ? 'primary.100' : 'primary.50'
                        }
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar 
                          sx={{ 
                            bgcolor: cabo.color,
                            width: 48,
                            height: 48
                          }}
                        >
                          <CaboIcon />
                        </Avatar>
                      </ListItemAvatar>

                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                              {cabo.titulo}
                            </Typography>
                            <Chip 
                              label={`${cabo.qtde_fo} FO${cabo.qtde_fo > 1 ? 's' : ''}`}
                              size="small"
                              sx={{ 
                                bgcolor: cabo.color,
                                color: 'white',
                                fontSize: '0.7rem'
                              }}
                            />
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="body2" color="text.secondary">
                              Fibras: {Array.from({ length: Math.min(cabo.qtde_fo, 6) }, (_, i) => `FO${i + 1}`).join(', ')}
                              {cabo.qtde_fo > 6 && ` ... FO${cabo.qtde_fo}`}
                            </Typography>
                            {cabo.pontos && cabo.pontos.length > 0 && (
                              <Typography variant="caption" color="text.secondary">
                                {cabo.pontos.length} ponto{cabo.pontos.length > 1 ? 's' : ''} no mapa
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))
              )}
            </List>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ pt: 0 }}>
        <Button onClick={handleClose}>
          Cancelar
        </Button>
        <Button 
          onClick={loadCabos} 
          disabled={loading}
          startIcon={<RefreshIcon />}
        >
          Atualizar Lista
        </Button>
        <Button 
          onClick={handleConfirmSelection}
          disabled={!selectedCabo}
          variant="contained"
          startIcon={<CaboIcon />}
        >
          {tipoCabo === TipoCabo.NORMAL && 'Adicionar Cabo Normal'}
          {tipoCabo === TipoCabo.PASSAGEM && 'Adicionar Cabos (Normal + Passagem)'}
          {tipoCabo === TipoCabo.SAIDA && 'Adicionar Cabo de Saída'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CaboSelectionModal; 